'use client'

import { useState } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'
import Avatar from 'boring-avatars'
import { formatImageURL } from '@/utils/util'
import { PASSWORD_RULES } from '@/utils/auth'
import { useCustomerSettings, useChangePassword } from '@/lib/commerce/hooks'

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.035)',
  border: '1px solid rgba(255,255,255,0.07)',
  color: 'white',
}

const labelClass = 'block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2'
const labelStyle: React.CSSProperties = { color: 'rgba(255,255,255,0.7)' }

type SectionKey = 'profile' | 'security'

export default function Settings({ customer, vendor }: { customer: any, vendor: any }) {
  void vendor
  const [section, setSection] = useState<SectionKey>('profile')
  const {
    firstName, setFirstName, lastName, setLastName, email, phone, setPhone,
    preview, loading, isImageUploaded, inputFile,
    handleFileChange, selectFile, handleSave,
  } = useCustomerSettings(customer)
  const {
    newPassword, setNewPassword, newPasswordConfirm, setNewPasswordConfirm,
    checks, isPasswordLoading, handlePasswordChange,
  } = useChangePassword()

  const navItems: { key: SectionKey, label: string }[] = [
    { key: 'profile', label: 'Хувийн мэдээлэл' },
    { key: 'security', label: 'Аюулгүй байдал' },
  ]

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12">
        <aside>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>Тохиргоо</div>
          <ul className="space-y-1">
            {navItems.map(item => {
              const active = section === item.key
              return (
                <li key={item.key}>
                  <button
                    onClick={() => setSection(item.key)}
                    className="block w-full text-left py-2 text-[13.5px] transition-colors"
                    style={{
                      color: active ? 'white' : 'rgba(255,255,255,0.55)',
                      fontWeight: active ? 600 : 400,
                      borderLeft: active ? '2px solid white' : '2px solid transparent',
                      paddingLeft: 12,
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              )
            })}
          </ul>
        </aside>

        <div className="min-w-0">
          {section === 'profile' ? (
            <div>
              <h2
                className="font-semibold tracking-[-0.02em] leading-none text-white mb-2"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(24px, 3vw, 32px)' }}
              >
                Хувийн мэдээлэл
              </h2>
              <p className="text-[13.5px] mb-10" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Энэхүү мэдээллийг захиалга, төлбөр, хүргэлтэд ашиглана.
              </p>

              <div className="flex items-center gap-5 mb-10">
                <button
                  type="button"
                  onClick={selectFile}
                  className="relative w-20 h-20 rounded-full overflow-hidden group cursor-pointer"
                  style={{ border: '1px solid rgba(255,255,255,0.14)' }}
                >
                  {preview ? (
                    <img src={preview} alt="profile" className="w-full h-full object-cover" />
                  ) : customer?.avatar ? (
                    <img src={formatImageURL(customer.avatar)} alt="profile" className="w-full h-full object-cover" />
                  ) : (
                    <Avatar size={80} variant="marble" name={customer?.email || undefined} />
                  )}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-[11px] font-medium">Солих</span>
                  </div>
                </button>
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={selectFile}
                    disabled={isImageUploaded}
                    className="h-9 px-4 rounded-full text-[12.5px] font-medium inline-flex justify-center items-center gap-2 transition-colors hover:bg-white/10 disabled:opacity-60"
                    style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                  >
                    {isImageUploaded && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {isImageUploaded ? 'Оруулж байна...' : 'Зураг солих'}
                  </button>
                  <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    PNG, JPG · 5MB хүртэл
                  </div>
                </div>
                <input type="file" accept="image/*" ref={inputFile} className="hidden" onChange={handleFileChange} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <label className={labelClass} style={labelStyle}>Нэр</label>
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Таны нэр"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Овог</label>
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Таны овог"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>И-мэйл</label>
                  <input
                    value={email}
                    disabled
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30 opacity-60 cursor-not-allowed"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Утасны дугаар</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    type="tel"
                    placeholder="00000000"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] tabular-nums outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div className="mt-10 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="h-11 px-6 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Хадгалах
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h2
                className="font-semibold tracking-[-0.02em] leading-none text-white mb-2"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(24px, 3vw, 32px)' }}
              >
                Аюулгүй байдал
              </h2>
              <p className="text-[13.5px] mb-10" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Нэвтрэх нууц үгээ шаардлагатай гэж үзвэл солих боломжтой.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <label className={labelClass} style={labelStyle}>Шинэ нууц үг</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Шинэ нууц үг"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Шинэ нууц үг давтах</label>
                  <input
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    placeholder="Шинэ нууц үг давтах"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {PASSWORD_RULES.map(rule => {
                  const ok = !!checks[rule.key]
                  return (
                    <div key={rule.key} className="flex items-center gap-2 text-[12.5px]">
                      <CheckCircle2
                        className="h-4 w-4 shrink-0"
                        style={{ color: ok ? '#34d399' : 'rgba(255,255,255,0.25)' }}
                      />
                      <span style={{ color: ok ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.55)' }}>{rule.label}</span>
                    </div>
                  )
                })}
              </div>

              <div className="mt-10 flex justify-end">
                <button
                  onClick={handlePasswordChange}
                  disabled={isPasswordLoading}
                  className="h-11 px-6 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  {isPasswordLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Хадгалах
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
